import React, { memo, useCallback, useMemo } from "react";
import CircularIconButton from "modules/_shared/buttons/circular-icon-button";
import {
  MessageSubType,
  MESSAGE_CALENDAR_FORMATS,
} from "modules/utils/Constants";
import { HelpIcon } from "modules/icons";

import { Text, VStack } from "@chakra-ui/react";
import colors from "app/colors";
import moment from "moment";
import { MessageThreadFragment, MessageFragment } from "gql/graphql";
import AddPendingEmailUsersButton from "./add-pending-email-users-button";
import { useOverlay } from "modules/providers/overlay-provider";
import { MODAL_TYPE } from "modules/providers/overlay-provider/overlay-provider";
import ViewThreadDetailsButton from "./view-thread-details-button";
import WelcomeVideo from "./welcome-video";
import MessagePlainText from "../message-plain-text";

interface IProps {
  messageThread: MessageThreadFragment;
  message: MessageFragment;
}

const SystemMessage = ({ messageThread, message }: IProps) => {
  const { openModal } = useOverlay();

  const messageModalType = useMemo(() => {
    switch (message.subType) {
      case MessageSubType.LOCKED: {
        if (messageThread.lockedUserId) {
          return MODAL_TYPE.LOCKED_CONVERSATION;
        }
        break;
      }
      case MessageSubType.UNLOCKED: {
        return MODAL_TYPE.UNLOCKED_CONVERSATION;
      }
      case MessageSubType.NON_MEMBER_EMAIL_USERS_ADDED: {
        return MODAL_TYPE.ADD_EXTERNAL_EMAIL_USERS;
      }
      default:
        return undefined;
    }
  }, [messageThread.lockedUserId, message.subType]);

  const modalId = useMemo(() => {
    switch (message.subType) {
      case MessageSubType.LOCKED: {
        return messageThread.lockedUserId || undefined;
      }
      default:
        return undefined;
    }
  }, [messageThread.lockedUserId, message.subType]);

  const messageAriaLabel = useMemo(() => {
    switch (message.subType) {
      case MessageSubType.LOCKED: {
        return "Lock conversation info";
      }
      case MessageSubType.UNLOCKED: {
        return "Unlock conversation info";
      }
      case MessageSubType.NON_MEMBER_EMAIL_USERS_ADDED: {
        return "External users info";
      }
      default:
        return "";
    }
  }, [message.subType]);

  const openMessageModal = useCallback(() => {
    if (!messageModalType) {
      return;
    }
    openModal({
      type: messageModalType,
      id: modalId,
    });
  }, [messageModalType, openModal, modalId]);

  return (
    <VStack w="full" alignItems="flex-start" pb={4} spacing={1}>
      <Text color={colors.gray50} fontWeight={400} fontSize={14}>
        <MessagePlainText
          text={message.text}
          highlight={false}
          suffix={
            <Text noOfLines={1} as="span">
              &nbsp;-&nbsp;
              {moment(message.createTime).calendar(MESSAGE_CALENDAR_FORMATS)}
              {messageModalType && (
                <CircularIconButton
                  icon={<HelpIcon fill={colors.gray50} />}
                  minW={6}
                  h={4}
                  aria-label={messageAriaLabel}
                  onClick={openMessageModal}
                />
              )}
            </Text>
          }
        />
      </Text>
      <AddPendingEmailUsersButton
        messageThread={messageThread}
        message={message}
      />
      <ViewThreadDetailsButton
        messageThread={messageThread}
        message={message}
      />
      <WelcomeVideo message={message} />
    </VStack>
  );
};

export default memo(SystemMessage);
